import { z } from 'zod';
import { CLASS_LEVELS, INDIAN_STATES } from '@/constants/institute';

/**
 * Validation for the create-institute wizard. Mirrors the backend's
 * `institutes.validation.ts` — the server still validates everything (CLAUDE.md
 * §10); these rules exist so the user sees a mistake on the step where they
 * made it rather than after five more steps.
 */

const requiredText = (label: string, max: number) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} is too long`);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'That is too long')
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined);


/**
 * One half of the campus pin. An emptied field sends `null`, so a pin can be
 * taken back off — and `''` is checked first because `z.coerce.number()`
 * turns it into 0, which is a real place in the ocean, not "no location".
 */
const coordinate = (label: string, limit: number) =>
  z
    .union([
      z.literal(''),
      z.coerce
        .number()
        .finite(`${label} must be a number`)
        .min(-limit, `${label} must be between -${limit} and ${limit}`)
        .max(limit, `${label} must be between -${limit} and ${limit}`),
    ])
    .optional()
    .transform((value) => (value === '' || value === undefined ? null : value));


/**
 * Ten digits, entered after a fixed +91 (`PhoneInput`), sent as `+91XXXXXXXXXX`.
 * Spaces, hyphens and a typed +91 are tolerated so a saved number still passes.
 * Mirrors `tenDigitPhone` in the backend's institutes.validation.ts.
 */
export const tenDigitPhone = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^(?:\+91)?\d{10}$/, 'Enter a 10-digit phone number'))
  .transform((value) => `+91${value.slice(-10)}`);

const optionalTenDigitPhone = z
  .union([z.literal(''), tenDigitPhone])
  .optional()
  .transform((value) => value || undefined);

export const email = z.string().trim().toLowerCase().email('Enter a valid email address').max(150);

const instituteFields = z.object({
  // --- step 1: identity & recognition ------------------------------------
  // Shown, never entered: the server assigns it when the institute is created
  // and it cannot change after. Optional so a form with nobody to fill it in
  // can still be submitted; the server does not accept it on either endpoint.
  code: z.string().optional(),
  subdomain: z
    .string()
    .trim()
    .toLowerCase()
    .max(40)
    .regex(/^[a-z0-9-]*$/, 'Lowercase letters, numbers and hyphens only')
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined),
  legalName: requiredText('Registered name', 180),
  displayName: requiredText('Display name', 120),
  shortName: optionalText(30),
  motto: optionalText(150),

  // Mirrors the server: optional, and an untouched field (undefined) is as
  // blank as a cleared one ("") rather than "Required".
  udiseCode: z
    .string()
    .trim()
    .transform((val) => val.replace(/\s+/g, ''))
    .pipe(z.string().regex(/^\d{11}$/, 'UDISE code is exactly 11 digits').or(z.literal('')))
    .optional()
    .transform((value) => value || undefined),
  /**
   * Institute or college. Everything institute-specific below is required only when
   * this says `SCHOOL` — a college has no board affiliation and no teaching
   * band, and demanding them would make the form unfillable.
   */
  institutionType: z.enum(['SCHOOL', 'COLLEGE']),
  board: z.enum(['CBSE', 'ICSE', 'STATE', 'IB', 'IGCSE', 'NIOS', 'OTHER']).optional(),
  stateBoardName: optionalText(80),
  affiliationNumber: optionalText(40),
  affiliationValidTill: z.string().optional().or(z.literal('')).transform((v) => v || undefined),
  recognitionNumber: optionalText(60),
  establishedYear: z
    .union([z.coerce.number().int().min(1800, 'Too early').max(new Date().getFullYear()), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  managementType: z.enum(['GOVERNMENT', 'AIDED', 'PRIVATE_UNAIDED', 'MINORITY', 'CENTRAL']),
  trustName: optionalText(180),
  trustRegistrationNo: optionalText(60),
  panNumber: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'PAN looks like ABCDE1234F')
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined),
  gstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/, 'That is not a valid GSTIN')
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined),
  /**
   * The authorised signatory's Aadhaar — twelve digits, never beginning 0 or
   * 1. Spaces are stripped rather than rejected, because the card prints it in
   * groups of four and that is how it gets typed.
   */
  aadhaarNumber: z
    .string()
    .trim()
    .transform((value) => value.replace(/\s/g, ''))
    .pipe(z.string().regex(/^[2-9]\d{11}$/, 'Aadhaar is 12 digits').or(z.literal('')))
    .optional()
    .transform((value) => value || undefined),

  // --- step 2: academic profile ------------------------------------------
  schoolLevel: z
    .enum(['PRE_PRIMARY', 'PRIMARY', 'UPPER_PRIMARY', 'SECONDARY', 'SENIOR_SECONDARY', 'K12'])
    .optional(),
  collegeLevel: z.enum(['INTERMEDIATE', 'UNDERGRADUATE', 'POSTGRADUATE', 'UG_PG']).optional(),
  lowestClass: z.enum(CLASS_LEVELS),
  highestClass: z.enum(CLASS_LEVELS),
  genderType: z.enum(['BOYS', 'GIRLS', 'CO_ED']),
  residentialType: z.enum(['DAY', 'BOARDING', 'DAY_BOARDING']),
  mediumOfInstruction: z
    .array(z.string())
    .transform((arr) => {
      const cleaned = arr.map((s) => s.trim()).filter(Boolean);
      const seen = new Set<string>();
      return cleaned.filter((item) => {
        const lower = item.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
    })
    .pipe(z.array(z.string()).min(1, 'Add at least one medium of instruction')),
  streams: z
    .array(z.string())
    .transform((arr) => {
      const cleaned = arr.map((s) => s.trim()).filter(Boolean);
      const seen = new Set<string>();
      return cleaned.filter((item) => {
        const lower = item.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
    })
    .default([]),

  // --- step 3: address & contact -----------------------------------------
  addressLine1: requiredText('Address', 180),
  addressLine2: optionalText(180),
  locality: optionalText(120),
  city: requiredText('City', 80),
  district: requiredText('District', 80),
  state: z.enum(INDIAN_STATES, { errorMap: () => ({ message: 'Select a state' }) }),
  pincode: z.string().trim().regex(/^[1-9]\d{5}$/, 'PIN code is 6 digits'),
  latitude: coordinate('Latitude', 90),
  longitude: coordinate('Longitude', 180),
  phone: tenDigitPhone,
  alternatePhone: optionalTenDigitPhone,
  whatsappNumber: optionalTenDigitPhone,
  email,
  website: z
    .string()
    .trim()
    .url('Include https://')
    .max(180)
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined),

  // --- step 4: people -----------------------------------------------------
  principalName: requiredText('Principal name', 120),
  principalPhone: tenDigitPhone,
  principalEmail: email,
  adminContactName: requiredText('Contact name', 120),
  adminContactDesignation: optionalText(80),
  adminContactPhone: tenDigitPhone,
  adminContactEmail: email,

  /**
   * What this institute pays for the software.
   *
   * Optional, because the fee is settled after the account is opened as often
   * as before it. An emptied field sends `null` rather than nothing, so a fee
   * typed by mistake can be taken back off the record — the server reads it
   * the same way.
   */
  subscriptionAmount: z
    .union([
      // Empty first: a union takes the first branch that accepts, and
      // `z.coerce.number()` accepts `''` by turning it into 0 — which would
      // save a fee of zero every time the field was cleared.
      z.literal(''),
      z.coerce
        .number()
        .finite('Enter an amount in rupees')
        .nonnegative('A fee cannot be negative')
        .max(9_999_999_999.99, 'That is larger than this field holds'),
    ])
    .optional()
    .transform((value) => (value === '' || value === undefined ? null : value)),
  paymentStatus: z.enum(['UNPAID', 'PAID']),
  /** Optional. An emptied field sends `null`, so a date can be taken back off. */
  subscriptionDueDate: z
    .string()
    .optional()
    .transform((value) => value || null),
});

/**
 * The rules that compare one answer with another, each on a schema of its own.
 *
 * Not `.refine` on the whole object: zod only runs those once *every* field is
 * valid, and while the wizard is on an early step the later ones are still
 * blank — so "lowest XII, highest X" went through the classes step with no
 * message at all. Picked out like this, each rule needs only the fields it
 * reads, and it reports on the step those fields are on.
 */
const classRangeRule = instituteFields
  .pick({ lowestClass: true, highestClass: true })
  .refine(
    (data) => CLASS_LEVELS.indexOf(data.lowestClass) <= CLASS_LEVELS.indexOf(data.highestClass),
    { path: ['highestClass'], message: 'Highest class must be the same or above the lowest' },
  );

const stateBoardRule = instituteFields
  .pick({ board: true, stateBoardName: true })
  .refine((data) => data.board !== 'STATE' || Boolean(data.stateBoardName), {
    path: ['stateBoardName'],
    message: 'Name the state board',
  });

// A pin is a pair — half of one is no place at all. Mirrors the server.
const coordinatePairRule = instituteFields
  .pick({ latitude: true, longitude: true })
  .refine((data) => (data.latitude === null) === (data.longitude === null), {
    path: ['longitude'],
    message: 'Give both latitude and longitude, or neither',
  });

/**
 * Required of an institute, meaningless for a college. Expressed here rather than
 * on the field because whether they are required depends on another answer —
 * and the server enforces exactly the same two rules, which is the one that
 * actually decides (CLAUDE.md §6).
 */
const schoolDetailsRule = instituteFields
  .pick({ institutionType: true, board: true, schoolLevel: true, collegeLevel: true })
  .refine((data) => data.institutionType !== 'SCHOOL' || Boolean(data.board), {
    path: ['board'],
    message: 'Choose the board this institute is affiliated to',
  })
  .refine((data) => data.institutionType !== 'SCHOOL' || Boolean(data.schoolLevel), {
    path: ['schoolLevel'],
    message: 'Choose the level this institute teaches',
  })
  .refine((data) => data.institutionType !== 'COLLEGE' || Boolean(data.collegeLevel), {
    path: ['collegeLevel'],
    message: 'Choose the level this college teaches',
  });

export const instituteSchema = instituteFields
  .and(classRangeRule)
  .and(stateBoardRule)
  .and(coordinatePairRule)
  .and(schoolDetailsRule);

export type InstituteFormValues = z.input<typeof instituteSchema>;
export type InstitutePayload = z.output<typeof instituteSchema>;

/**
 * The payload with a college's school-only answers taken out.
 *
 * The form hides the board, state board and school level once College is
 * chosen, but their defaults (CBSE, K-12) stay in the form state — and were
 * sent and stored for every college. A college is created without them; the
 * server drops them too, so an older client cannot store them either.
 */
export function withoutSchoolOnlyFields(payload: InstitutePayload): InstitutePayload {
  if (payload.institutionType !== 'COLLEGE') return payload;
  return { ...payload, board: undefined, stateBoardName: undefined, schoolLevel: undefined };
}

/**
 * Which fields belong to which step, so the wizard can validate only the step
 * being left instead of the whole form.
 */
export const STEP_FIELDS = [
  [
    'subdomain',
    'legalName',
    'displayName',
    'shortName',
    'motto',
    'udiseCode',
    'institutionType',
    'board',
    'stateBoardName',
    'affiliationNumber',
    'affiliationValidTill',
    'recognitionNumber',
    'establishedYear',
    'managementType',
    'trustName',
    'trustRegistrationNo',
    'panNumber',
    'gstin',
    'aadhaarNumber',
  ],
  [
    'schoolLevel',
    'collegeLevel',
    'lowestClass',
    'highestClass',
    'genderType',
    'residentialType',
    'mediumOfInstruction',
    'streams',
  ],
  [
    'addressLine1',
    'addressLine2',
    'locality',
    'city',
    'district',
    'state',
    'pincode',
    'latitude',
    'longitude',
    'phone',
    'alternatePhone',
    'whatsappNumber',
    'email',
    'website',
  ],
  [
    'principalName',
    'principalPhone',
    'principalEmail',
    'adminContactName',
    'adminContactDesignation',
    'adminContactPhone',
    'adminContactEmail',
    // The licence fee sits on the same step as the person we bill — the
    // commercial end of the wizard, rather than a fifth screen for two fields.
    'subscriptionAmount',
    'paymentStatus',
    'subscriptionDueDate',
  ],
] as const satisfies readonly (readonly (keyof InstituteFormValues)[])[];

/**
 * The optional fields an edit can empty.
 *
 * The form's own coercions turn a blank into `undefined`, which is right for
 * creating ("not set") but on an update means "leave it alone" — so emptying
 * one of these used to save nothing and still report "saved". On an edit a
 * blank one is sent as `null`, which the server reads as "clear it".
 *
 * Not the Aadhaar number: its box is shown empty over a saved number, so blank
 * there means "keep". Not the selects and lists either — they are never blank
 * on an institute that passed creation.
 */
const CLEARABLE_FIELDS = [
  'subdomain',
  'shortName',
  'motto',
  'udiseCode',
  'stateBoardName',
  'affiliationNumber',
  'affiliationValidTill',
  'recognitionNumber',
  'establishedYear',
  'trustName',
  'trustRegistrationNo',
  'panNumber',
  'gstin',
  'addressLine2',
  'locality',
  'alternatePhone',
  'whatsappNumber',
  'website',
  'adminContactDesignation',
] as const satisfies readonly (keyof InstitutePayload)[];

type ClearableField = (typeof CLEARABLE_FIELDS)[number];

/** An edit's body: any subset of fields, where `null` clears an optional one. */
export type InstituteUpdatePayload = Partial<Omit<InstitutePayload, ClearableField>> & {
  [Field in ClearableField]?: InstitutePayload[Field] | null;
};

/**
 * One step's answers as the edit screen sends them — the same coercions as the
 * whole form (digits → +91…), for that step's fields only, with an emptied
 * optional field sent as `null` (see `CLEARABLE_FIELDS`). The edit screen saves
 * a tab at a time, and the server's update takes any subset.
 */
export function stepPayload(step: number, values: InstituteFormValues): InstituteUpdatePayload {
  const mask: { [Field in keyof typeof instituteFields.shape]?: true } = {};
  for (const field of STEP_FIELDS[step] ?? []) mask[field] = true;
  const payload: InstituteUpdatePayload = instituteFields.pick(mask).parse(values);

  for (const field of CLEARABLE_FIELDS) {
    if (mask[field] && payload[field] === undefined) payload[field] = null;
  }
  return payload;
}

/**
 * A whole form's answers as an edit, with every emptied optional field sent as
 * `null` — for a screen that saves the full form at once (the school's own
 * profile), where a blank must clear the stored value, not leave it.
 */
export function withClearedFields(payload: Partial<InstitutePayload>): InstituteUpdatePayload {
  const update: InstituteUpdatePayload = { ...payload };
  for (const field of CLEARABLE_FIELDS) {
    if (update[field] === undefined) update[field] = null;
  }
  return update;
}

export const WIZARD_STEPS = [
  { key: 'identity', label: 'Identity' },
  { key: 'academic', label: 'Academic' },
  { key: 'contact', label: 'Address' },
  { key: 'people', label: 'People' },
  { key: 'review', label: 'Review' },
];
